// Messages.tsx

import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useSearchParams } from "react-router-dom";
import InfiniteScroll from 'react-infinite-scroll-component';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { BsChevronDoubleDown } from "react-icons/bs";
import { Button } from 'flowbite-react';
import { toast } from 'react-hot-toast';
import imageCompression from 'browser-image-compression';

// Local Imports
import { useContextState } from '../../context/Context';
import { useMessagesWs, useMessagesApi } from "../../store";
import Loader from '../shared/Loader';
import AlertComponent from '../shared/AlertComponent';
import SingleMessage from './SingleMessage';
import ChatInput from './chatInput';
// ImagesUpload and Modal are no longer needed here
import '../styles.css'

const Messages: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const inboxId = searchParams.get('id');

    const { accountCTX } = useContextState();
    const { profile } = accountCTX;
    const { getChatSocket } = useMessagesWs();
    const { getMessages } = useMessagesApi();

    const [messages, setMessages] = useState<any[]>([]);
    const [messageText, setMessageText] = useState<string>(''); // State for the input is still here
    
    const [subject, setSubject] = useState<string>('');
    const [inboxIcon, setInboxIcon] = useState<string>('');
    const [page, setPage] = useState<number>(2);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Renamed for clarity: this now represents the "sending" state for the input
    const [isSending, setIsSending] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const ws = useRef<WebSocket | null>(null);
    const scrollableDivRef = useRef<HTMLDivElement>(null);

    // loadMoreHistory and useEffect for WebSocket connection remain unchanged...
    const loadMoreHistory = useCallback(async () => {
        if (!hasMore || !inboxId) return;
        try {
            const apiCall = await getMessages({ id:inboxId, page:page });
            if (apiCall?.results) {
                setMessages(prev => [...prev, ...apiCall.results]);
                setHasMore(Boolean(apiCall.next));
                if (apiCall.next) {
                    setPage(prev => prev + 1);
                }
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Failed to fetch more messages:", err);
        }
    }, [page, inboxId, hasMore, getMessages]);

    useEffect(() => {
        if (!inboxId || ws.current) return;
        const chatSocket = getChatSocket(inboxId);
        ws.current = chatSocket;
        chatSocket.onopen = () => {
            chatSocket.send(JSON.stringify({ type: 'get_messages' }));
            chatSocket.send(JSON.stringify({ type: 'join_room' }));
        };
        chatSocket.onmessage = (e: MessageEvent) => {
            const data = JSON.parse(e.data);
            switch (data.type) {
                case "messages":
                    setMessages(data.messages || []);
                    setHasMore(Boolean(data.next));
                    setPage(data.next || 2);
                    setIsLoading(false);
                    break;
                case "chat.message":
                    setMessages(prev => [data.message, ...prev]);
                    break;
                case "user_join":
                    if (data.inbox) {
                        setSubject(data.inbox.subject);
                        setInboxIcon(data.inbox.icon);
                    }
                    break;
                default:
                    break;
            }
        };
        chatSocket.onerror = (e: Event) => {
            console.error('WebSocket error:', e);
            setError("Could not connect to the chat service.");
            setIsLoading(false);
        };
        chatSocket.onclose = () => {
            if (typeof window !== 'undefined') window.history.back();
        };
        return () => {
            if (ws.current?.readyState === WebSocket.OPEN) {
                ws.current.close();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inboxId]);


    // --- REFACTORED: The new, unified onSend function ---
    const onSend = async (text: string, file: File | null) => {
        if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
            return toast.error("Connection not available.");
        }
        if (!text.trim() && !file) {
            return; // Don't send empty messages
        }

        setIsSending(true);

        // Case 1: Message includes an image
        if (file) {
            try {
                // Compress the image before sending
                const options = { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true };
                const compressedFile = await imageCompression(file, options);
                
                // Convert to Base64
                const reader = new FileReader();
                reader.readAsDataURL(compressedFile);
                reader.onloadend = () => {
                    const base64String = reader.result as string;
                    ws.current?.send(JSON.stringify({
                        type: 'message',
                        message: text, // The caption
                        image_data: base64String,
                        filename: compressedFile.name
                    }));
                    setMessageText(''); // Clear input after sending
                    setIsSending(false);
                };
                reader.onerror = () => {
                    toast.error("Could not process image.");
                    setIsSending(false);
                };
            } catch (error) {
                toast.error("Failed to compress image.");
                setIsSending(false);
            }
        } 
        // Case 2: Message is text only
        else {
            ws.current.send(JSON.stringify({
                type: 'message',
                message: text,
            }));
            setMessageText(''); // Clear input after sending
            setIsSending(false);
        }
    };
    
    // handleBack and scrollToBottom remain unchanged...
    const handleBack = () => {
        if (ws.current) {
            ws.current.close();
        } else {
            if (typeof window !== 'undefined') window.history.back();
        }
    };

    const scrollToBottom = () => {
        scrollableDivRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (isLoading) {
        return <div className="mt-20"><Loader /></div>;
    }

    if (error) {
        return <AlertComponent callBack={handleBack} type='error' subject='Connection Failed' message={error} />;
    }

    return (
        <div className="fixed mx-auto inset-x-0 sm:w-1/2 flex flex-col bg-gray-50 h-[calc(100dvh-74px)]">
            {/* Header remains the same */}
            <header className="flex-shrink-0 w-full rounded-lg bg-gray-900 flex items-center justify-between pb-0 pt-1 border-b-2 border-gray-200 z-10">
                <div className="flex items-center">
                    <button type="button" onClick={handleBack} className="inline-flex items-center justify-center rounded-lg h-10 w-10 text-gray-100 hover:bg-gray-700">
                        <IoIosArrowRoundBack className="h-8 w-8 text-gray-100" />
                    </button>
                </div>
                <div className="flex flex-col items-center leading-tight">
                    <img src={inboxIcon || "/logo.png"} alt={`${subject || "Messages"} icon`} className="w-10 h-10 rounded-full object-cover" />
                    <span className="text-sm font-semibold text-gray-100 mt-1">{subject || "Messages"}</span>
                </div>
                <div className="h-10 w-10"></div>
            </header>

            {/* Message List Area remains the same */}
            <main id="scrollableDiv" ref={scrollableDivRef} className="flex-1 min-h-0 overflow-y-auto flex flex-col-reverse p-3 bg-contain bg-gray-200 bg-opacity-10 scrollbar-thumb-blue scrollbar-thumb-rounded scrollbar-track-blue-lighter scrollbar-w-2 scrolling-touch" style={{ backgroundImage: `url('/images/TransparentLogo.png')` }}>
                <InfiniteScroll
                    dataLength={messages.length}
                    next={loadMoreHistory}
                    hasMore={hasMore}
                    inverse={true}
                    loader={<div className="py-4"><Loader /></div>}
                    scrollableTarget="scrollableDiv"
                    style={{ display: 'flex', flexDirection: 'column-reverse' }}
                    endMessage={<p className="text-center text-sm text-gray-500 py-4">Beginning of the conversation</p>}
                >
                    {messages.map((message, index) => {
                        const messageDate = message.created_at ? new Date(message.created_at).toLocaleDateString() : '';
                        const nextMessage = messages[index + 1];
                        const nextMessageDate = nextMessage?.created_at ? new Date(nextMessage.created_at).toLocaleDateString() : '';
                        const showDateSeparator = messageDate && messageDate !== nextMessageDate;
                        return (
                            <React.Fragment key={message.id}>
                                <SingleMessage message={message} isMe={message.user === profile.username} />
                                {showDateSeparator && (
                                    <div className="mt-3 mb-3 w-full flex justify-between items-center ">
                                        <hr className="w-full h-px bg-gray-300 border-0" />
                                        <span className="text-sm bg-gray-200 rounded-full text-gray-900 px-4 whitespace-nowrap">{messageDate}</span>
                                        <hr className="w-full h-px bg-gray-300 border-0" />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </InfiniteScroll>
            </main>
            
            {/* Scroll to Bottom Button remains the same */}
            <Button onClick={scrollToBottom} type="button" color="gray" pill className="fixed bottom-40 right-4 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 z-20 shadow-lg">
                <BsChevronDoubleDown size={18} />
            </Button>

            {/* --- MODIFIED: Input Area now uses the new ChatInput --- */}
            <footer className="flex-shrink-0 border-t-2 bg-gray-200 border-gray-300 p-4">
                <ChatInput 
                    onSend={onSend}
                    message={messageText}
                    onMessageChange={setMessageText}
                    isSending={isSending}
                />
            </footer>

            {/* The Modal and ImagesUpload component are no longer rendered here */}
        </div>
    );
};

export default Messages;